// RewardsSummary.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_CONFIG from "../../lib/ApiConfig";
import { useAuth } from "../../hooks/useAuth";
import RewardsModal from "./RewardsModal";
import Stacked from "../../assets/images/omig-coins-stacked.png";
import { ExternalLink, LoaderCircle } from "lucide-react";
import BuyerDetailsModal from "./BuyerDetailsModal";
import RewardsService from "../../lib/RewardsService";
import { formatCurrency, formatMonthYear } from "../../lib/utils";
import OwnliScreenWrapper, { OwnliButton, OwnliCard } from "../ownli/OwnliScreenWrapper";
import toast from "react-hot-toast";
import ConfirmModal from "../ConfirmModal";
import UserService from "../../lib/UserService";
import track from "../../lib/analytics/events";

const RewardsSummary = () => {
  const navigate = useNavigate();
  const { getIdToken, user, loading: authLoading } = useAuth();
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [notEnoughBalanceModalOpen, setNotEnoughBalanceModalOpen] = useState(
    false
  );

  const [claimLoader, setClaimLoader] = useState(true);
  const [canClaim, setCanClaim] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    total: 0,
    confirmed: 0,
    pending: 0,
  });

  const [ownliUser, setOwnliUser] = useState({});
  const [totalRewards, setTotalRewards] = useState([]);
  const [rewardType, setRewardType] = useState("Total");
  const [rewardsModalOpen, setRewardsModalOpen] = useState(false);
  const [rewardsModalLoading, setRewardsModalLoading] = useState(true);

  const [activeBuyerId, setActiveBuyerId] = useState(null);
  const [buyerModalOpen, setBuyerModalOpen] = useState(false);

  const loadSummary = async () => {
    // Only attempt API call when auth is ready and we have a user
    if (authLoading || !user) return;

    setLoading(true);
    try {
      const token = await getIdToken();
      let path = API_CONFIG.ENDPOINTS.USER.REWARDS_SUMMARY;
      let url = API_CONFIG.getInternalUrl(path);

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-user-token": token,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setSummary(
          data || {
            total: 0,
            confirmed: 0,
            pending: 0,
          }
        );
      }

      path = API_CONFIG.ENDPOINTS.USER.CAN_CLAIM_REWARDS;
      url = API_CONFIG.getInternalUrl(path);

      const responseCanClaim = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-user-token": token,
        },
      });
      if (responseCanClaim.status === 200) {
        setCanClaim(true);
      }
    } catch (err) {
      console.error("Error loading rewards summary:", err);
    } finally {
      setLoading(false);
      setClaimLoader(false);
    }
  };

  

  // -------- Load rewards summary (Vue: load()) ----------
  useEffect(() => {
    const loadUser = async () => {
      const token = await getIdToken();
      const serverUserData = await UserService.getUserIfExists(token);
      setOwnliUser(serverUserData);
    };

    loadSummary();
    loadUser();
  }, [getIdToken, user, authLoading]);

  const openRewardsModal = async (type = "Total") => {
    track.rewardsBalanceModalOpened({ filter: type.toLowerCase() });
    setRewardType(type);
    setRewardsModalOpen(true);
    setRewardsModalLoading(true);

    try {
      const token = await getIdToken();
      const url = API_CONFIG.getInternalUrl(`/api/rewards/list`);

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-user-token": token,
        },
      });

      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length) {
          let filtered = [];
          switch (type) {
            case "Pending":
              filtered = data.filter((r) => r.status === "Pending");
              break;
            case "Unclaimed":
              filtered = data.filter((r) => r.status === "Confirmed");
              break;
            case "Total":
            default:
              filtered = data.filter(
                (r) => r.status === "Confirmed" || r.status === "Claimed"
              );
              break;
          }
          setTotalRewards(filtered);
        } else {
          setTotalRewards([]);
        }
      }
    } catch (err) {
      console.error("Error loading rewards list:", err);
    } finally {
      setRewardsModalLoading(false);
    }
  };

  const openBuyerModal = (buyerId) => {
    track.buyerDetailsOpened({ buyer_id: buyerId });
    setActiveBuyerId(buyerId);
    setBuyerModalOpen(true);
  };

  const dismissBuyerModal = () => {
    setActiveBuyerId(null);
    setBuyerModalOpen(false);
  };

  const goToProfile = () => navigate("/contact-info");

  const handleClaimRewards = () => {
    track.custom("nw_claim_button_clicked", { can_claim: canClaim });
    // Phone/email verification gate commented out per product request — relying on can-claim API instead
    // const emailVerified = ownliUser?.emailVerified;
    // const phoneVerified = ownliUser?.phoneVerified;
    //
    // if (!emailVerified || !phoneVerified) {
    //   // Open verification modal if not verified
    //   track.custom("nw_claim_denied_contact_not_verified");
    //   setVerificationModalOpen(true);
    // } else {
    if (canClaim) {
      goToClaimRewards();
    } else {
      track.claimDeniedNoBalance();
      setNotEnoughBalanceModalOpen(true);
    }
    // }
  };
  const goToClaimRewards = () => navigate("/rewards-claim");

  return (
    <>
      <OwnliScreenWrapper className="px-0">
        <div className="max-w-3xl mx-auto space-y-8">
          <OwnliCard
            variant="primary"
            className="relative !bg-crimsonGlory text-white shadow-xl rounded-md"
          >
            <div className={loading ? "opacity-0" : "opacity-100"}>
              <div className="space-y-1">
                <h2 className="text-lg font-medium">Balance</h2>
                {ownliUser?.dateRegistered && (
                  <p className="text-xs md:text-sm text-white/80">
                    Member since {formatMonthYear(ownliUser?.dateRegistered)}
                  </p>
                )}
              </div>

              <div className="mt-4 space-y-4 pr-28 md:pr-40">
                <div className="space-y-1">
                  <p className="text-sm text-white/90">
                    Earned (including Unclaimed)
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-3xl font-bold">
                      {formatCurrency(summary?.total, "")}
                    </span>
                    <button
                      type="button"
                      className="text-xs underline underline-offset-2 text-white/90 hover:text-white"
                      onClick={() => openRewardsModal("Total")}
                    >
                      <ExternalLink className="!w-4 !h-4 mb-1.5" />
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-10 mt-2">
                  <div className="space-y-1">
                    <p className="text-xs md:text-sm text-white/80">
                      Unclaimed
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-base md:text-lg font-semibold">
                        {formatCurrency(summary?.confirmed, "")}
                      </span>
                      <button
                        type="button"
                        className="text-[11px] md:text-xs underline text-white/90 hover:text-white"
                        onClick={() => openRewardsModal("Unclaimed")}
                      >
                        <ExternalLink className="!w-4 !h-4 mb-1.5" />
                      </button>
                    </div>
                  </div>

                  <div className="h-10 w-px bg-white/40" />

                  <div className="space-y-1">
                    <p className="text-xs md:text-sm text-white/80">Pending</p>
                    <div className="flex items-center gap-2">
                      <span className="text-base md:text-lg font-semibold">
                        {formatCurrency(summary?.pending, "")}
                      </span>
                      <button
                        type="button"
                        className="text-[11px] md:text-xs underline text-white/90 hover:text-white"
                        onClick={() => openRewardsModal("Pending")}
                      >
                        <ExternalLink className="!w-4 !h-4 mb-1.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <img
                src={Stacked}
                alt="Rewards"
                className=" sm:block absolute right-4 bottom-0 w-14 md:w-24"
              />
            </div>

            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                <div className="flex flex-col items-center gap-2">
                  <LoaderCircle className="!w-6 !h-6 animate-spin" />
                  <p className="text-white text-sm">Loading rewards summary…</p>
                </div>
              </div>
            )}
          </OwnliCard>

          <p className="text-veryDarkGrey text-base">
            Please confirm your{" "}
            <button
              type="button"
              onClick={goToProfile}
              className="text-crimsonGlory underline underline-offset-2"
            >
              contact info
            </button>{" "}
            in order to securely receive your rewards.
          </p>

          <div className="flex justify-center">
            <OwnliButton
              onClick={handleClaimRewards}
              className="w-max px-12 shadow-lg"
            >
              {claimLoader ? "Loading..." : "Claim rewards"}
            </OwnliButton>
          </div>

          {/* Verification Modal — commented out per product request, relying on can-claim API instead
          <ConfirmModal
            open={verificationModalOpen}
            onOpenChange={setVerificationModalOpen}
            title="Verify Your Contact Information"
            onConfirm={() => {
              // Close modal and navigate to profile
              setVerificationModalOpen(false);
              goToProfile();
            }}
            description={
              <p className="text-veryDarkGrey text-xs md:text-sm mb-4">
                Minimum to claim is $1 and requires verified{" "}
                <span className="text-crimsonGlory">email</span> and{" "}
                <span className="text-crimsonGlory">cell phone</span>.
              </p>
            }
            confirmText="Go to Profile"
            cancelText="Cancel"
          />
          */}

          {/* Not Enought Balance Modal */}
          <ConfirmModal
            open={notEnoughBalanceModalOpen}
            onOpenChange={setNotEnoughBalanceModalOpen}
            title="Minimum to claim is $1"
            onConfirm={() => {
              // Close modal and navigate to profile
              setNotEnoughBalanceModalOpen(false);
            }}
            description={
              <p className="text-veryDarkGrey text-sm md:text-sm mb-4">
                Minimum to claim is{" "}
                <span className="text-crimsonGlory">$1</span>
                {". "}
                <div>
                  If you think this is an error please contact us at{" "}
                  <a
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-crimsonGlory"
                    href="https://support.ownli.co/hc/en-us/requests/new"
                  >
                    Ownli Support
                  </a>
                </div>
                {/* <span className="text-crimsonGlory">email</span> and{" "}
                <span className="text-crimsonGlory">cell phone</span>. */}
              </p>
            }
            confirmText="OK"
          />

          <p className="text-veryDarkGrey text-xs md:text-sm">
            Minimum to claim is $1 and requires verified{" "}
            <button
              type="button"
              onClick={goToProfile}
              className="text-crimsonGlory underline underline-offset-2"
            >
              email
            </button>{" "}
            and{" "}
            <button
              type="button"
              onClick={goToProfile}
              className="text-crimsonGlory underline underline-offset-2"
            >
              cell phone
            </button>
            .
          </p>
        </div>
      </OwnliScreenWrapper>

      {rewardsModalOpen && (
        <RewardsModal
          currency={""}
          open={rewardsModalOpen}
          rewardType={rewardType}
          totalRewards={totalRewards}
          loading={rewardsModalLoading}
          onSelectBuyer={openBuyerModal}
          onOpenChange={setRewardsModalOpen}
        />
      )}

      {buyerModalOpen && activeBuyerId && (
        <BuyerDetailsModal
          buyerId={activeBuyerId}
          onDismiss={dismissBuyerModal}
        />
      )}      
    </>
  );
};

export default RewardsSummary;
